import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type { CustomFieldValueMap } from './customFields';
import type { Availability } from '@/lib/availability';

/*
 * Clients API — two endpoints, two shapes.
 *
 * - `useClientsList()` hits GET /api/clients?view=list. The response is a
 *   trimmed shape (see ClientListRow) suitable for the list table.
 *   The full client record is never sent over the wire for list views —
 *   HIPAA minimum-necessary.
 *
 * - `useClient(id)` hits GET /api/clients/:id and returns the full record.
 *   Used by the detail page. The server writes a "Client viewed" audit
 *   row on every successful fetch.
 */

export interface ClientAuthSummary {
    hours: number;
    used: number;
    end: string | null;
    /** Derived on the server: Active | Expiring | Expired | Pending. */
    status: 'Active' | 'Expiring' | 'Expired' | 'Pending';
}

export interface ClientListRow {
    id: string;
    first: string;
    last: string;
    dob: string | null;
    /** Intake / onboarding date (`YYYY-MM-DD`) — the day services started. */
    intake: string | null;
    dx1: string;
    dx2: string;
    status: string;
    /** Primary payer name, extracted from ins1.payer server-side. */
    payer: string;
    auth: ClientAuthSummary | null;
    /** FK to the assigned BCBA user. Drives the staff filter. */
    bcbaId: string | null;
    /** Resolved BCBA full name for display. Empty string when unassigned. */
    bcba: string;
    /**
     * Distinct user IDs of the BCBA *and* any additional staff assignments.
     * The staff filter matches if the selected user appears in this array.
     */
    staffUserIds: string[];
    /**
     * Weekly availability pattern used by the scheduler to warn when a
     * session falls outside the client's window. Only present when the
     * caller has edit_scheduling — omitted for clinical-only roles.
     */
    availability?: Availability;
}

export interface ClientAuth {
    /** Per-entry UUID used by PATCH/DELETE /:id/auths/:entryId. */
    id: string;
    payer: string;
    number: string;
    code: string;
    hours: number;
    used: number;
    start: string;
    end: string;
}

export interface ClientStaff {
    /** Per-entry UUID used by DELETE /:id/staff/:entryId. */
    id: string;
    /** FK to users — what drives the `view_assigned_clients` permission. */
    userId: string | null;
    /** Resolved at read time from the users table; don't treat as a snapshot. */
    name: string;
    role: string;
    start: string;
    status: string;
}

export type ContactLogType =
    | 'Phone Call'
    | 'Email'
    | 'Text Message'
    | 'In-Person Meeting'
    | 'Session Note Follow-Up'
    | 'Portal Message'
    | 'Other';

export const CONTACT_LOG_TYPES: ContactLogType[] = [
    'Phone Call',
    'Email',
    'Text Message',
    'In-Person Meeting',
    'Session Note Follow-Up',
    'Portal Message',
    'Other',
];

export interface ClientContactLogEntry {
    id: string;
    date: string;
    by: string;
    type: ContactLogType;
    note: string;
}

export interface ClientHistoryEntry {
    date: string;
    from: string;
    to: string;
    by: string;
    note: string;
}

// Subscriber address sub-object stored inside ClientInsurance. Populated
// when the subscriber is different from the client (837P subscriber loop
// 2010BA requires address for non-self relationships).
export interface ClientInsuranceSubAddress {
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
}

// Subscriber relationship code — maps to 837P SBR*02. The canonical
// set we support today: self, spouse, child, other.
export type ClientInsuranceSubRel = 'self' | 'spouse' | 'child' | 'other';

export interface ClientInsurance {
    payer?: string;
    id?: string;
    group?: string;
    /** Subscriber name ("First Last") when different from the client. */
    sub?: string;
    /** Subscriber DOB (YYYY-MM-DD). Required by 837P when sub ≠ self. */
    subDob?: string;
    /** Subscriber sex code — 'M' | 'F' | 'U'. */
    subGender?: string;
    /** Relationship of the subscriber to the client (patient). */
    subRel?: ClientInsuranceSubRel;
    subAddress?: ClientInsuranceSubAddress;
}

export interface ClientRecord {
    id: string;
    first: string;
    last: string;
    dob: string | null;
    gender: string;
    referralSource: string;
    status: string;
    intake: string | null;
    address: string;
    city: string;
    state: string;
    zip: string;
    phone: string;
    dx1: string;
    dx2: string;
    /** Diagnosis FK — null for legacy free-text entries not matched to the catalog. */
    dx1Id: string | null;
    dx2Id: string | null;
    dx1Code: string;
    dx2Code: string;
    dx1Date: string | null;
    dx2Date: string | null;
    dx1ProviderName: string;
    dx1ProviderNpi: string;
    dx2ProviderName: string;
    dx2ProviderNpi: string;
    developmentalHistory: string;
    medical: string;
    medications: string;
    allergies: string;
    familyMedicalHistory: string;
    reasonForReferral: string;
    otherServices: string;
    communityServices: string;
    coordinationOfCare: string;
    education: string;
    schoolSchedule: string;
    culturalConsiderations: string;
    bcbaId: string | null;
    bcba: string;
    gname: string;
    grel: string;
    gphone: string;
    gemail: string;
    emergencyContact: { name?: string; rel?: string; phone?: string; email?: string };
    ins1: ClientInsurance;
    ins2: ClientInsurance | null;
    auths: ClientAuth[];
    staff: ClientStaff[];
    docs: { name: string; type: string; by: string; date: string }[];
    contactLog: ClientContactLogEntry[];
    history: ClientHistoryEntry[];
    customFields: Record<string, unknown>;
    availability: Availability;
}

async function fetchClientsList(): Promise<ClientListRow[]> {
    const res = await api.get<ClientListRow[]>('/clients', { params: { view: 'list' } });
    return res.data;
}

async function fetchClient(id: string): Promise<ClientRecord> {
    const res = await api.get<ClientRecord>(`/clients/${id}`);
    return res.data;
}

export function useClientsList() {
    return useQuery({
        queryKey: ['clients', 'list'] as const,
        queryFn: fetchClientsList,
        // 5 min — the list is used on scheduling, analytics, and ~8 reporting
        // pages; navigating between them should not trigger a fresh fetch for
        // the same list. Client mutations invalidate this key explicitly, so
        // stale-tolerance here doesn't risk showing out-of-date data.
        staleTime: 5 * 60_000,
    });
}

/*
 * Lightweight picker source for every page that just wants a client
 * <select>. Returns `[{ id, first, last, status }]` — no payer, no
 * auth, no staffUserIds, no availability. Hits GET /api/clients/names.
 *
 * Use this — not `useClientsList()` — anywhere the UI's only need is
 * a dropdown / combo box of client names. Reserve `useClientsList()`
 * for the actual list table, the scheduling dialog (needs `auth` +
 * `availability` for warnings), and the availability-overlap report
 * (needs `availability`). Everything else picks here.
 */

export interface ClientNameRow {
    id: string;
    first: string;
    last: string;
    status: string;
}

async function fetchClientNames(): Promise<ClientNameRow[]> {
    const res = await api.get<ClientNameRow[]>('/clients/names');
    return res.data;
}

export function useClientNames() {
    return useQuery({
        queryKey: ['clients', 'names'] as const,
        queryFn: fetchClientNames,
        // Same 5-min window as the heavier list — the picker shouldn't
        // refetch on every tab navigation, and the `['clients']` prefix
        // on mutation invalidations already covers this key.
        staleTime: 5 * 60_000,
    });
}

export interface ClientAuthAlertRow {
    id: string;
    first: string;
    last: string;
    /** Always 'Active' on this slim row — the server filters before serialising —
     * but typed loosely so the dashboard alert builder can keep the
     * defense-in-depth check that lived there pre-cutover. */
    status: string;
    /** Intake date as 'YYYY-MM-DD'. Used by the dashboard's "new clients in
     * the last 7 days" alert. */
    intake: string | null;
    auth: import('./clients').ClientAuthSummary | null;
}

export interface ClientCountsResponse {
    byStatus: Record<string, number>;
    total: number;
    /** Slim Active-client rows for dashboard auth-expiry alerts. */
    authRows: ClientAuthAlertRow[];
}

async function fetchClientsCounts(): Promise<ClientCountsResponse> {
    const res = await api.get<ClientCountsResponse>('/clients/counts');
    return res.data;
}

export function useClientsCounts() {
    return useQuery({
        queryKey: ['clients', 'counts'] as const,
        queryFn: fetchClientsCounts,
        staleTime: 5 * 60_000,
    });
}

export function useClient(id: string | undefined) {
    return useQuery({
        queryKey: ['client', id] as const,
        queryFn: () => fetchClient(id!),
        enabled: !!id,
    });
}

/*
 * Create / edit payloads.
 *
 * The server accepts every ClientRecord field on POST / PATCH, but we
 * only surface a subset in the UI — the "minimum to get a record on
 * the books" on create (first, last, dob, dx1, status, optionally
 * bcbaId), and the fleshed-out demographics + clinical + guardian +
 * emergency + insurance set on edit. Auths, staff, and contact log
 * have their own add flows on the detail page.
 */

export interface ClientCreatePayload {
    first: string;
    last: string;
    dob: string;
    /** UUID from the diagnoses reference-data catalog. */
    dx1Id: string;
    status: 'Active' | 'Pending' | 'Waitlist';
    bcbaId?: string | null;
}

export interface ClientUpdatePayload {
    first?: string;
    last?: string;
    dob?: string | null;
    gender?: string;
    referralSource?: string;
    status?: string;
    intake?: string | null;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    phone?: string;
    dx1Id?: string | null;
    dx2Id?: string | null;
    dx1Date?: string | null;
    dx2Date?: string | null;
    dx1ProviderName?: string;
    dx1ProviderNpi?: string;
    dx2ProviderName?: string;
    dx2ProviderNpi?: string;
    developmentalHistory?: string;
    medical?: string;
    medications?: string;
    allergies?: string;
    familyMedicalHistory?: string;
    reasonForReferral?: string;
    otherServices?: string;
    communityServices?: string;
    coordinationOfCare?: string;
    education?: string;
    schoolSchedule?: string;
    culturalConsiderations?: string;
    bcbaId?: string | null;
    gname?: string;
    grel?: string;
    gphone?: string;
    gemail?: string;
    emergencyContact?: { name?: string; rel?: string; phone?: string; email?: string };
    ins1?: ClientInsurance;
    ins2?: ClientInsurance | null;
    customFields?: CustomFieldValueMap;
    availability?: Availability;
}

async function createClient(payload: ClientCreatePayload): Promise<ClientRecord> {
    const res = await api.post<ClientRecord>('/clients', payload);
    return res.data;
}

async function updateClient(id: string, payload: ClientUpdatePayload): Promise<ClientRecord> {
    const res = await api.patch<ClientRecord>(`/clients/${id}`, payload);
    return res.data;
}

export function useCreateClient() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: createClient,
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['clients'] });
        },
    });
}

export function useUpdateClient(id: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload: ClientUpdatePayload) => updateClient(id, payload),
        onSuccess: (data) => {
            qc.setQueryData(['client', id], data);
            qc.invalidateQueries({ queryKey: ['clients'] });
        },
    });
}

/*
 * Status changes are funnelled through a dedicated endpoint so the history
 * JSONB array, the status column, and the audit row all land atomically.
 * The generic PATCH endpoint no longer accepts `status`; the client can't
 * bypass this by crafting a different request.
 */

export type ClientStatusOption = 'Active' | 'Pending' | 'Waitlist' | 'Inactive';

/*
 * sessionHandling is required when `status === 'Inactive'` and rejected on
 * every other transition. `delete` removes every scheduled session for the
 * client regardless of date; rows in a preserved status (Complete,
 * Finalized, Canceled, Cancelled, No Show) are untouched.
 */
export type SessionHandlingOption = 'keep' | 'delete';

export interface ClientStatusChangePayload {
    status: ClientStatusOption;
    note?: string;
    sessionHandling?: SessionHandlingOption;
}

export interface ClientStatusChangeResult extends ClientRecord {
    /** Count of scheduled sessions the server deleted, or 0. */
    affectedSessionCount: number;
}

/** Counts surfaced by GET /:id/deactivation-preview — no PHI. */
export interface DeactivationPreview {
    scheduledCount: number;
    notesInProgressCount: number;
}

async function fetchDeactivationPreview(id: string): Promise<DeactivationPreview> {
    const res = await api.get<DeactivationPreview>(`/clients/${id}/deactivation-preview`);
    return res.data;
}

export function useDeactivationPreview(id: string | undefined, enabled: boolean) {
    return useQuery({
        queryKey: ['client', id, 'deactivation-preview'] as const,
        queryFn: () => fetchDeactivationPreview(id!),
        enabled: !!id && enabled,
        staleTime: 0,
    });
}

async function changeClientStatus(
    id: string,
    payload: ClientStatusChangePayload,
): Promise<ClientStatusChangeResult> {
    const res = await api.post<ClientStatusChangeResult>(`/clients/${id}/status-change`, payload);
    return res.data;
}

export function useChangeClientStatus(id: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload: ClientStatusChangePayload) => changeClientStatus(id, payload),
        onSuccess: (data) => {
            const { affectedSessionCount: _omit, ...clientRecord } = data;
            qc.setQueryData(['client', id], clientRecord);
            qc.invalidateQueries({ queryKey: ['clients'] });
            if (data.affectedSessionCount > 0) {
                // Session rows were deleted — invalidate any cached session
                // lists (calendar, session-activity report, etc.) so the UI
                // picks the change up without a hard refresh.
                qc.invalidateQueries({ queryKey: ['sessions'] });
            }
        },
    });
}

/*
 * Contact log mutations. Every add / edit / delete hits a dedicated
 * endpoint that writes an audit row inside the same withTenant
 * transaction — contact notes routinely contain PHI. The response
 * is just the entry (or 204 on delete), so on success we refetch
 * the client record to keep `contactLog` in sync without having
 * to replay the mutation locally.
 */

export interface ContactLogAddPayload {
    date: string;
    type: ContactLogType;
    note: string;
    by?: string;
}

export interface ContactLogEditPayload {
    date?: string;
    type?: ContactLogType;
    note?: string;
    by?: string;
}

async function addContactLogEntry(
    clientId: string,
    payload: ContactLogAddPayload,
): Promise<ClientContactLogEntry> {
    const res = await api.post<ClientContactLogEntry>(
        `/clients/${clientId}/contact-log`, payload,
    );
    return res.data;
}

async function editContactLogEntry(
    clientId: string,
    entryId: string,
    payload: ContactLogEditPayload,
): Promise<ClientContactLogEntry> {
    const res = await api.patch<ClientContactLogEntry>(
        `/clients/${clientId}/contact-log/${entryId}`, payload,
    );
    return res.data;
}

async function deleteContactLogEntry(clientId: string, entryId: string): Promise<void> {
    await api.delete(`/clients/${clientId}/contact-log/${entryId}`);
}

export function useAddContactLogEntry(clientId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload: ContactLogAddPayload) => addContactLogEntry(clientId, payload),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['client', clientId] });
        },
    });
}

export function useEditContactLogEntry(clientId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ entryId, payload }: { entryId: string; payload: ContactLogEditPayload }) =>
            editContactLogEntry(clientId, entryId, payload),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['client', clientId] });
        },
    });
}

export function useDeleteContactLogEntry(clientId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (entryId: string) => deleteContactLogEntry(clientId, entryId),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['client', clientId] });
        },
    });
}

/*
 * Staff assignments. Only two operations — add and remove — because the
 * FK (`userId`) drives the `view_assigned_clients` permission and an
 * edit of that FK is indistinguishable from remove-then-add. `role`,
 * `start`, and `status` are free-text snapshots for display; changing
 * one is cheap enough to model as "remove + re-add" without a dedicated
 * PATCH. Every mutation writes an audit row server-side.
 *
 * The generic PATCH /api/clients does not accept `staff`.
 */

export type ClientStaffStatus = 'Active' | 'Inactive';

export interface ClientStaffAddPayload {
    userId: string;
    role?: string;
    start?: string;
    status?: ClientStaffStatus;
}

async function addClientStaff(
    clientId: string,
    payload: ClientStaffAddPayload,
): Promise<ClientStaff> {
    const res = await api.post<ClientStaff>(`/clients/${clientId}/staff`, payload);
    return res.data;
}

async function removeClientStaff(clientId: string, entryId: string): Promise<void> {
    await api.delete(`/clients/${clientId}/staff/${entryId}`);
}

export function useAddClientStaff(clientId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload: ClientStaffAddPayload) => addClientStaff(clientId, payload),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['client', clientId] });
        },
    });
}

export function useRemoveClientStaff(clientId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (entryId: string) => removeClientStaff(clientId, entryId),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['client', clientId] });
        },
    });
}

/*
 * Authorizations — payer, auth number, CPT code, hours, and date range.
 * PHI/billing identifiers, so every add / edit / delete hits a dedicated
 * endpoint that writes an audit row. The generic PATCH /api/clients does
 * not accept `auths`.
 */

export interface ClientAuthAddPayload {
    payer?: string;
    number?: string;
    code?: string;
    hours?: number;
    used?: number;
    start?: string;
    end?: string;
}

export type ClientAuthEditPayload = ClientAuthAddPayload;

async function addClientAuth(
    clientId: string,
    payload: ClientAuthAddPayload,
): Promise<ClientAuth> {
    const res = await api.post<ClientAuth>(`/clients/${clientId}/auths`, payload);
    return res.data;
}

async function editClientAuth(
    clientId: string,
    entryId: string,
    payload: ClientAuthEditPayload,
): Promise<ClientAuth> {
    const res = await api.patch<ClientAuth>(
        `/clients/${clientId}/auths/${entryId}`, payload,
    );
    return res.data;
}

async function removeClientAuth(clientId: string, entryId: string): Promise<void> {
    await api.delete(`/clients/${clientId}/auths/${entryId}`);
}

export function useAddClientAuth(clientId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload: ClientAuthAddPayload) => addClientAuth(clientId, payload),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['client', clientId] });
            qc.invalidateQueries({ queryKey: ['clients'] });
        },
    });
}

export function useEditClientAuth(clientId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ entryId, payload }: { entryId: string; payload: ClientAuthEditPayload }) =>
            editClientAuth(clientId, entryId, payload),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['client', clientId] });
            qc.invalidateQueries({ queryKey: ['clients'] });
        },
    });
}

export function useRemoveClientAuth(clientId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (entryId: string) => removeClientAuth(clientId, entryId),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['client', clientId] });
            qc.invalidateQueries({ queryKey: ['clients'] });
        },
    });
}
